import React from "react";

import { Account } from "src/types/accounts";
import { MenuState, SettingsBanner } from "src/types/settings/accountManagement";
import { animated, useChain, useSpringRef, useTransition } from "@react-spring/web";
import { Banner } from "src/types/common";
import { BannerView } from "src/components/banner";
import { Menubar } from "../components/menuBar";
import  DescriptionEditor  from "../components/description";
import { BannerEditor } from "../components/banner/library";
import { BannerFilters } from "../components/banner/filter";
import { CropEditor } from "../components/crop";

import DealRoomWidgetManager from "src/utils/dealRoom.widget.service";

import _ from "lodash"
import cx from "classnames";
import styles from "./styles.module.scss";

;
interface DealRoomSettingsProps {
  data: Partial<Account>;
  handleAccountSettingsUpdate: (field: string, value: any) => void;
  resetEditor?: boolean;
}

const DealRoomSettings: React.FC<DealRoomSettingsProps> = ({ data, handleAccountSettingsUpdate, resetEditor }) => {

  const [menuState, setMenuState] = React.useState<MenuState>({
    primaryAction: 'view'
  });

  const showBanner = menuState.primaryAction === 'edit-banner' && !menuState.secondaryAction;
  const cropBanner = menuState.primaryAction === 'edit-banner' && menuState.secondaryAction === 'crop';
  const showFilters = menuState.primaryAction === 'edit-banner' && menuState.secondaryAction === 'filter';
  const showPosition = menuState.primaryAction === 'edit-banner' && menuState.secondaryAction === 'position';

  const bannerActive = showBanner || showFilters || showPosition || cropBanner;

  const bannerEditorRef = useSpringRef();
  const bannerEditorTransitions = useTransition(showBanner, {
    ref: bannerEditorRef,
    initial: { opacity: 1 },
    from: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0px, 0)' },
    config: { friction: 14, tension: 120 },
  });

  const bannerFilterRef = useSpringRef();
  const bannerFilterTransitions = useTransition(showFilters, {
    ref: bannerFilterRef,
    initial: { opacity: 1 },
    from: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    enter: { opacity: 1, transform: 'translate3d(0, 0px, 0)' },
    leave: { opacity: 0, transform: 'translate3d(0, 30vh, 0)' },
    config: { friction: 14, tension: 120 },
  });

  const cropperRef = useSpringRef();
  const cropperTransitions = useTransition(cropBanner, {
    ref: cropperRef,
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    delay: showBanner ? 0 : 0,
    config: { friction: 14, tension: 120 },
  });

  const handleBannerChange = React.useCallback(
    (field: string, updatedBanner: Partial<Banner>) => {
      if (field === 'banner') {
        const currentSettings: Partial<Account['dealRoomSettings']> = _.get(data, 'dealRoomSettings') || {};
        const updatedSettings = {
          ...currentSettings,
          banner: {
            ...(currentSettings.banner || {}),
            ...updatedBanner,
          },
        };
        handleAccountSettingsUpdate('dealRoomSettings', updatedSettings);
      }
    },
    [data, handleAccountSettingsUpdate]
  );

  const handleFilterChange = (field, newValue) => {
    const updatedSettings = {
      ...data.dealRoomSettings,
      [field]: newValue,
    };
    handleAccountSettingsUpdate('dealRoomSettings', updatedSettings);
  };

  useChain([bannerEditorRef, bannerFilterRef, cropperRef], [0, 0, 0]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.heading}>Default Banner</div>
        <div className={styles.description}>
          Set the visual identity for your Deal Room.
        </div>
      </div>

      <div className={styles.bannerWrapper}>
      <div
            className={cx(styles.banner, {
              [styles.hide]: bannerActive,
              [styles.cropping]: cropBanner,
              [styles.show]: !bannerActive
            })}
          >
          {!cropBanner && (
            <BannerView
              banner={_.get(data, "dealRoomSettings.banner") as SettingsBanner | undefined}
              defaultBanner="/images/collections/background.jpeg"
            />
          )}
           </div>
           <div
            className={cx(styles.menuBarWrapperTop, {
              [styles.menuBarShifted]: bannerActive,
            })}
          >
            <Menubar
              entitySettings={data.dealRoomSettings}
              menuState={menuState}
              onChange={setMenuState}
              onChanges={handleAccountSettingsUpdate}
              enableLiveToggle={false}
              entityField="dealRoomSettings"
            />
          </div>
        </div>

      {menuState.primaryAction === 'view' && (
      <div className={styles.editorOverlay}>
        <DescriptionEditor
          key={resetEditor ? "reset" : "stable"}
          editorContent={_.get(data, "dealRoomSettings.description")}
          widgetManager={new DealRoomWidgetManager()}
          maxHeight={menuState.primaryAction === "view" ? "none" : "100%"}
          menuState={{ primaryAction: "view" }}
          onChange={(value) => {
            const current = data.dealRoomSettings || {};
            handleAccountSettingsUpdate("dealRoomSettings", { ...current, description: value });
          }}
          onMenuChange={(newState) => setMenuState(newState)}
        />
      </div>
      )}

      <div className={styles.body}>
        {bannerEditorTransitions((style, show) => show && (
          <animated.div style={style} className={cx(styles.content, styles.bannerEditor)}>
            <BannerEditor
              collection={_.get(data, "dealRoomSettings")}
              onChange={handleBannerChange}
            />
          </animated.div>
        ))}

        {bannerFilterTransitions((style, show) => show && (
          <animated.div style={style} className={styles.content}>
            {data?.dealRoomSettings && (
            <BannerFilters
              collection={data.dealRoomSettings}
              onChange={handleFilterChange}
            />
            )}
          </animated.div>
        ))}

        {cropperTransitions((style, show) => show && (
          <animated.div style={style} className={styles.editor}>
            <CropEditor
              enabled
              entity={_.get(data, "dealRoomSettings")}
              menuState={menuState}
              onMenuChange={setMenuState}
              onChange={handleFilterChange}
            />
          </animated.div>
        ))}
      </div>
    </div>
  );
};

export default DealRoomSettings;
